import { forwardRef, useState, type ChangeEvent, type InputHTMLAttributes } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type TextInputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  error?: string;
};

/** Labeled input with optional inline error — accepts all HTML input props.
 *  Compatible with React Hook Form's `{...register("field")}` spread.
 *  Shows a live "used/max" counter whenever `maxLength` is set. */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  ({ label, error, id, maxLength, value, defaultValue, onChange, ...props }, ref) => {
    // Uncontrolled (RHF register, no `value`) fields track length locally; controlled ones read it from `value`.
    const isControlled = value !== undefined;
    const [uncontrolledLength, setUncontrolledLength] = useState(
      () => String(defaultValue ?? "").length
    );
    const length = isControlled ? String(value ?? "").length : uncontrolledLength;

    return (
      <div className="space-y-1.5">
        {label && <Label htmlFor={id}>{label}</Label>}
        <Input
          ref={ref}
          id={id}
          maxLength={maxLength}
          value={value}
          defaultValue={defaultValue}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            if (!isControlled) setUncontrolledLength(e.target.value.length);
            onChange?.(e);
          }}
          {...props}
        />
        {error && <p className="text-sm text-destructive">{error}</p>}
        {typeof maxLength === "number" && (
          <p
            className={`text-xs text-right ${
              length > maxLength ? "text-destructive" : "text-muted-foreground"
            }`}
          >
            {length}/{maxLength}
          </p>
        )}
      </div>
    );
  }
);
TextInput.displayName = "TextInput";
